/* iter70 — Detailed Production Script + Character Dialogue Map panel.
 *
 * Renders inside the Storyboard tab, immediately ABOVE the Production
 * Continuity panel. Provides Generate / Revise (with Director Notes) /
 * Approve / Reject + a version-history strip. Never auto-creates
 * Production Continuity items — the "Create draft character /
 * environment" actions call the existing iter69 routes and require
 * explicit operator clicks.
 *
 * Hard rules honoured:
 *   - No mutation of stages.script.*
 *   - No mutation of Storyboard / Prompt Pack / Video Studio.
 *   - No paid API calls.
 *   - No video generation.
 *   - Panel never crashes when no detailed script exists.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
    FileText, Sparkles, RefreshCw, CheckCircle2, X as XIcon,
    Plus, Loader2, AlertTriangle, ChevronDown, ChevronUp,
    UserPlus, MapPinned, Clock,
} from "lucide-react";
import api from "@/lib/api";

const STATUS_PILLS = {
    draft:      "border-zinc-700 text-zinc-300 bg-zinc-900",
    approved:   "border-emerald-500/40 text-emerald-300 bg-emerald-500/10",
    rejected:   "border-red-500/40 text-red-300 bg-red-500/10",
    superseded: "border-amber-500/40 text-amber-300 bg-amber-500/10",
};

function StatusPill({ status }) {
    const cls = STATUS_PILLS[status] || STATUS_PILLS.draft;
    return (
        <span
            className={`text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded-sm border ${cls}`}
            data-testid={`dps-status-${status || "draft"}`}
        >
            {status || "draft"}
        </span>
    );
}

function Section({ title, count, children, defaultOpen = true, testId }) {
    const [open, setOpen] = useState(defaultOpen);
    return (
        <div
            className="border border-zinc-800 rounded-sm bg-zinc-950/60"
            data-testid={testId}
        >
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                className="w-full flex items-center justify-between px-3 py-2 hover:bg-zinc-900/50 transition"
            >
                <div className="flex items-center gap-2">
                    <span className="font-display text-sm text-zinc-100">{title}</span>
                    {typeof count === "number" && (
                        <span className="text-[10px] font-mono text-zinc-500 uppercase tracking-wider">
                            {count}
                        </span>
                    )}
                </div>
                {open
                    ? <ChevronUp className="w-3.5 h-3.5 text-zinc-500" />
                    : <ChevronDown className="w-3.5 h-3.5 text-zinc-500" />}
            </button>
            {open && (
                <div className="px-3 pb-3 pt-1">
                    {children}
                </div>
            )}
        </div>
    );
}

function TimecodedTable({ beats }) {
    if (!beats?.length) {
        return (
            <p className="text-[11px] text-zinc-500 italic">
                No timecoded breakdown returned.
            </p>
        );
    }
    return (
        <div className="overflow-x-auto" data-testid="dps-timecoded-table">
            <table className="w-full text-[11px]">
                <thead>
                    <tr className="text-left text-zinc-500 uppercase tracking-wider text-[10px] font-mono border-b border-zinc-800">
                        <th className="py-1 pr-2">Beat</th>
                        <th className="py-1 pr-2">Time</th>
                        <th className="py-1 pr-2">Scene</th>
                        <th className="py-1 pr-2">Visual action</th>
                        <th className="py-1 pr-2">On-screen</th>
                        <th className="py-1 pr-2">Speaker</th>
                        <th className="py-1 pr-2">Dialogue / VO</th>
                        <th className="py-1 pr-2">Env</th>
                    </tr>
                </thead>
                <tbody>
                    {beats.map((b, i) => (
                        <tr key={b.beat_id || i} className="border-b border-zinc-900 align-top"
                            data-testid={`dps-beat-row-${b.beat_id || i}`}>
                            <td className="py-1 pr-2 font-mono text-zinc-300">{b.beat_id}</td>
                            <td className="py-1 pr-2 text-zinc-400">
                                {b.start_seconds ?? "?"}–{b.end_seconds ?? "?"}s
                            </td>
                            <td className="py-1 pr-2 text-zinc-300">{b.scene_label}</td>
                            <td className="py-1 pr-2 text-zinc-300">{b.visual_action}</td>
                            <td className="py-1 pr-2 text-zinc-400 font-mono text-[10px]">
                                {(b.on_screen_characters || []).join(", ")}
                            </td>
                            <td className="py-1 pr-2 text-zinc-300 font-mono text-[10px]">
                                {b.speaking_character || ""}
                            </td>
                            <td className="py-1 pr-2 text-zinc-300">{b.dialogue_or_vo}</td>
                            <td className="py-1 pr-2 text-zinc-400 font-mono text-[10px]">
                                {b.environment || ""}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

function CharacterMap({ chars, voiceoverOnly }) {
    const all = [
        ...(chars || []),
        ...(voiceoverOnly || []).map((v) => ({ ...v, character_kind: v.character_kind || "voiceover_only" })),
    ];
    if (!all.length) {
        return (
            <p className="text-[11px] text-zinc-500 italic">
                No characters identified by the agent yet.
            </p>
        );
    }
    return (
        <div className="space-y-1" data-testid="dps-character-map">
            {all.map((c, i) => (
                <div
                    key={c.character_id || i}
                    className="flex items-start gap-2 text-[11px] border border-zinc-800 rounded-sm bg-zinc-950 px-2 py-1"
                    data-testid={`dps-character-row-${c.character_id || i}`}
                >
                    <span className="font-mono text-zinc-400 text-[10px] uppercase tracking-wider min-w-[8rem]">
                        {c.character_kind || "visual_character"}
                    </span>
                    <span className="font-display text-zinc-100">{c.display_name || c.character_id}</span>
                    <span className="text-zinc-500 font-mono text-[10px]">({c.character_id})</span>
                    <span className="text-zinc-400 ml-2 truncate">{c.short_description}</span>
                </div>
            ))}
        </div>
    );
}

function EnvironmentMap({ envs }) {
    if (!envs?.length) {
        return (
            <p className="text-[11px] text-zinc-500 italic">
                No environments identified yet.
            </p>
        );
    }
    return (
        <div className="space-y-1" data-testid="dps-environment-map">
            {envs.map((e, i) => (
                <div
                    key={e.environment_id || i}
                    className="flex items-start gap-2 text-[11px] border border-zinc-800 rounded-sm bg-zinc-950 px-2 py-1"
                    data-testid={`dps-env-row-${e.environment_id || i}`}
                >
                    <span className="font-display text-zinc-100">{e.display_name || e.environment_id}</span>
                    <span className="text-zinc-500 font-mono text-[10px]">({e.environment_id})</span>
                    <span className="text-zinc-400">{e.location_type}</span>
                    <span className="text-zinc-500 ml-2 truncate">{e.visual_mood}</span>
                </div>
            ))}
        </div>
    );
}

function ReferenceRequirementMap({ refs }) {
    if (!refs?.length) {
        return (
            <p className="text-[11px] text-zinc-500 italic">
                No reference assets explicitly required.
            </p>
        );
    }
    return (
        <div className="space-y-1" data-testid="dps-reference-map">
            {refs.map((r, i) => (
                <div
                    key={i}
                    className="flex items-start gap-2 text-[11px] border border-zinc-800 rounded-sm bg-zinc-950 px-2 py-1"
                    data-testid={`dps-ref-row-${i}`}
                >
                    <span className={`text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded-sm border ${
                        r.required_before_storyboard
                            ? "border-amber-500/40 text-amber-300 bg-amber-500/10"
                            : "border-zinc-700 text-zinc-400 bg-zinc-900"
                    }`}>
                        {r.required_before_storyboard ? "REQUIRED" : "optional"}
                    </span>
                    <span className="font-mono text-zinc-400 text-[10px]">{r.reference_kind}</span>
                    <span className="font-mono text-zinc-500 text-[10px]">{r.target_id}</span>
                    <span className="text-zinc-300">{r.description}</span>
                </div>
            ))}
        </div>
    );
}

function ContinuityHandoffSuggestions({ projectId, suggestions, onChanged }) {
    const chars = suggestions?.characters || [];
    const envs = suggestions?.environments || [];
    const [busyId, setBusyId] = useState(null);

    const createDraftCharacter = async (s) => {
        setBusyId(`char-${s.suggested_id || s.display_name}`);
        try {
            await api.post(
                `/projects/${projectId}/production-continuity/characters`,
                {
                    name: s.display_name || s.suggested_id,
                    role: s.character_kind || "",
                    wardrobe: s.wardrobe_hint || "",
                    do_not_change_rules: [],
                    reference_asset_ids: [],
                    approval_status: "draft",
                },
            );
            toast.success(`Draft character "${s.display_name || s.suggested_id}" created.`);
            onChanged?.();
        } catch (e) {
            toast.error(e?.userMessage || "Could not create draft character");
        } finally {
            setBusyId(null);
        }
    };

    const createDraftEnvironment = async (s) => {
        setBusyId(`env-${s.suggested_id || s.display_name}`);
        try {
            await api.post(
                `/projects/${projectId}/production-continuity/environments`,
                {
                    name: s.display_name || s.suggested_id,
                    location_type: s.location_type || "",
                    visual_mood: s.visual_mood || "",
                    camera_friendly_notes: s.short_description || "",
                    approval_status: "draft",
                },
            );
            toast.success(`Draft environment "${s.display_name || s.suggested_id}" created.`);
            onChanged?.();
        } catch (e) {
            toast.error(e?.userMessage || "Could not create draft environment");
        } finally {
            setBusyId(null);
        }
    };

    if (!chars.length && !envs.length) {
        return (
            <p className="text-[11px] text-zinc-500 italic">
                No continuity hand-off suggestions in this revision.
            </p>
        );
    }
    return (
        <div className="space-y-2" data-testid="dps-handoff-suggestions">
            <p className="text-[10px] text-zinc-500 italic">
                Suggestions only — nothing is auto-approved. Each &quot;Create draft&quot; button
                writes a DRAFT entry to the Production Continuity bible that you must
                review and approve below.
            </p>
            {chars.length > 0 && (
                <div>
                    <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 mb-1">
                        Suggested characters
                    </div>
                    <div className="space-y-1">
                        {chars.map((s, i) => {
                            const key = `char-${s.suggested_id || s.display_name || i}`;
                            return (
                                <div key={i} className="flex items-center gap-2 text-[11px] border border-zinc-800 rounded-sm bg-zinc-950 px-2 py-1"
                                     data-testid={`dps-suggest-char-${s.suggested_id || i}`}>
                                    <span className="font-display text-zinc-100">{s.display_name || s.suggested_id}</span>
                                    <span className="text-zinc-500 font-mono text-[10px]">({s.character_kind})</span>
                                    <span className="text-zinc-400 truncate flex-1">{s.short_description}</span>
                                    <button
                                        type="button"
                                        onClick={() => createDraftCharacter(s)}
                                        disabled={busyId === key}
                                        className="btn-secondary text-[10px] px-2 py-0.5"
                                        data-testid={`dps-create-draft-char-${s.suggested_id || i}`}
                                    >
                                        {busyId === key
                                            ? <Loader2 className="w-3 h-3 animate-spin" />
                                            : <UserPlus className="w-3 h-3" />}
                                        Create draft character
                                    </button>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}
            {envs.length > 0 && (
                <div>
                    <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 mb-1">
                        Suggested environments
                    </div>
                    <div className="space-y-1">
                        {envs.map((s, i) => {
                            const key = `env-${s.suggested_id || s.display_name || i}`;
                            return (
                                <div key={i} className="flex items-center gap-2 text-[11px] border border-zinc-800 rounded-sm bg-zinc-950 px-2 py-1"
                                     data-testid={`dps-suggest-env-${s.suggested_id || i}`}>
                                    <span className="font-display text-zinc-100">{s.display_name || s.suggested_id}</span>
                                    <span className="text-zinc-500 font-mono text-[10px]">({s.location_type})</span>
                                    <span className="text-zinc-400 truncate flex-1">{s.short_description}</span>
                                    <button
                                        type="button"
                                        onClick={() => createDraftEnvironment(s)}
                                        disabled={busyId === key}
                                        className="btn-secondary text-[10px] px-2 py-0.5"
                                        data-testid={`dps-create-draft-env-${s.suggested_id || i}`}
                                    >
                                        {busyId === key
                                            ? <Loader2 className="w-3 h-3 animate-spin" />
                                            : <MapPinned className="w-3 h-3" />}
                                        Create draft environment
                                    </button>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
}

export default function DetailedProductionScriptPanel({ projectId, onHandoffCreated }) {
    const [current, setCurrent] = useState(null);
    const [summary, setSummary] = useState({});
    const [versions, setVersions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [generating, setGenerating] = useState(false);
    const [revising, setRevising] = useState(false);
    const [approving, setApproving] = useState(false);
    const [directorNote, setDirectorNote] = useState("");
    const [explicitVariant, setExplicitVariant] = useState("");
    const [allowDraft, setAllowDraft] = useState(false);
    // iter72 — async DPS job polling state.
    // `activeJob`: { job_id, kind, status, started_at, elapsed_seconds }
    const [activeJob, setActiveJob] = useState(null);

    const load = useCallback(async () => {
        if (!projectId) return;
        setLoading(true);
        try {
            const [cur, ver] = await Promise.all([
                api.get(`/projects/${projectId}/detailed-production-script`),
                api.get(`/projects/${projectId}/detailed-production-script/versions`),
            ]);
            setCurrent(cur.data?.current || null);
            setSummary(cur.data?.summary || {});
            setVersions(ver.data?.versions || []);
        } catch (e) {
            toast.error(e?.userMessage || "Could not load detailed production script");
        } finally {
            setLoading(false);
        }
    }, [projectId]);

    useEffect(() => { load(); }, [load]);

    // iter72 — Poll the background DPS job until it reaches a terminal
    // state. Triggered from generate() and revise() once they return a
    // job_id. Idempotent — polling stops on succeeded/failed.
    const pollJob = useCallback(async (jobId, kind) => {
        const POLL_INTERVAL_MS = 2500;
        const MAX_POLL_MS = 5 * 60 * 1000;          // 5 minutes hard cap
        const startedAtMs = Date.now();
        // Local tick that keeps the elapsed-seconds counter alive even when
        // the network poll is paused (helps UX during slow networks).
        let cancelled = false;
        const tick = () => {
            if (cancelled) return;
            setActiveJob((prev) => prev && prev.job_id === jobId
                ? { ...prev, elapsed_seconds: Math.floor((Date.now() - startedAtMs) / 1000) }
                : prev);
        };
        const tickHandle = setInterval(tick, 1000);
        try {
            while (!cancelled) {
                if (Date.now() - startedAtMs > MAX_POLL_MS) {
                    setActiveJob(null);
                    toast.error("Job timed out client-side after 5 minutes. Refresh to check if the revision was saved.");
                    await load();
                    return;
                }
                try {
                    const { data } = await api.get(
                        `/projects/${projectId}/detailed-production-script/job/${jobId}`,
                    );
                    setActiveJob((prev) => prev && prev.job_id === jobId
                        ? { ...prev, status: data.status, elapsed_seconds: Math.floor((Date.now() - startedAtMs) / 1000) }
                        : prev);
                    if (data.status === "succeeded") {
                        setCurrent(data.doc || null);
                        setSummary(data.summary || {});
                        setActiveJob(null);
                        setDirectorNote("");
                        toast.success(
                            kind === "generate"
                                ? "Detailed Production Script generated (revision 1)."
                                : `Detailed Production Script revised (revision ${data.doc?.revision_number || "?"}).`,
                        );
                        await load();
                        return;
                    }
                    if (data.status === "failed") {
                        setActiveJob(null);
                        const code = data.error_code || "internal_error";
                        const msg = data.error || "Generation failed";
                        toast.error(msg + (code === "budget_exceeded" ? "" : " · Click " + (kind === "generate" ? "Generate" : "Revise") + " to retry."));
                        // The error may be a budget cap — refresh project state so
                        // any admin self-heal hooks (Phase N.2) get a chance.
                        await load();
                        return;
                    }
                } catch (pollErr) {
                    // Transient network blip — don't kill the poll loop, just
                    // back off a bit.
                    const status = pollErr?.response?.status;
                    if (status === 404) {
                        setActiveJob(null);
                        toast.error("Job not found. The pod may have restarted — refresh to check if a revision was saved.");
                        await load();
                        return;
                    }
                }
                await new Promise((res) => setTimeout(res, POLL_INTERVAL_MS));
            }
        } finally {
            cancelled = true;
            clearInterval(tickHandle);
        }
    }, [projectId, load]);

    const generate = async () => {
        if (activeJob || generating) return;
        setGenerating(true);
        try {
            const { data } = await api.post(
                `/projects/${projectId}/detailed-production-script/generate`,
                {
                    source_script_variant: explicitVariant.trim() || "",
                    allow_draft: allowDraft,
                    director_note: directorNote.trim(),
                    // iter70.1 — explicit paid-LLM spend confirmation.
                    // Clicking the button = explicit operator intent.
                    confirm_llm_spend: true,
                },
            );
            // iter72 — fast queued response. Start polling.
            const jobId = data?.job_id;
            if (!jobId) {
                toast.error("Backend did not return a job id. Refresh to retry.");
                return;
            }
            setActiveJob({
                job_id: jobId,
                kind: "generate",
                status: data?.status || "queued",
                started_at: data?.started_at || new Date().toISOString(),
                elapsed_seconds: 0,
            });
            toast.success("Generating Detailed Production Script…");
            // Fire-and-forget; the poll loop manages its own lifecycle.
            pollJob(jobId, "generate");
        } catch (e) {
            const code = e?.response?.data?.detail?.code;
            const detail = e?.response?.data?.detail || {};
            const msg = detail.message || e?.userMessage;
            if (code === "dps_already_running") {
                // iter72 — adopt the existing job instead of failing the click.
                // iter72.1 — cross-kind aware: the existing job may be a
                // revise (not a generate) for the same project+source, so
                // we adopt whichever kind the backend reports.
                const existingId = detail.existing_job_id;
                const existingKind = detail.kind || "generate";
                if (existingId) {
                    setActiveJob({
                        job_id: existingId,
                        kind: existingKind,
                        status: detail.status || "running",
                        started_at: detail.started_at || new Date().toISOString(),
                        elapsed_seconds: 0,
                    });
                    toast.info(
                        existingKind === "revise"
                            ? "A revision is already running for this project and source. Watching the existing job."
                            : "A generation is already running for this project and source. Watching the existing job.",
                    );
                    pollJob(existingId, existingKind);
                    return;
                }
            }
            if (code === "variant_not_approved") {
                toast.error(msg || "Approve a script variant first, or tick 'Allow draft'.");
            } else if (code === "multi_approved_no_pick") {
                toast.error(msg || "Multiple approved variants — pick one explicitly.");
            } else if (code === "no_script_output") {
                toast.error(msg || "Run the Script agent first.");
            } else if (code === "confirm_llm_spend_required") {
                toast.error(msg || "Paid LLM spend confirmation required.");
            } else {
                toast.error(msg || "Generation failed");
            }
        } finally {
            setGenerating(false);
        }
    };

    const revise = async () => {
        if (activeJob || revising) return;
        if (!directorNote.trim()) {
            toast.error("Director note is required for a revision.");
            return;
        }
        setRevising(true);
        try {
            const { data } = await api.post(
                `/projects/${projectId}/detailed-production-script/revise`,
                {
                    director_note: directorNote.trim(),
                    allow_draft: allowDraft,
                    // iter70.1 — explicit paid-LLM spend confirmation.
                    confirm_llm_spend: true,
                },
            );
            const jobId = data?.job_id;
            if (!jobId) {
                toast.error("Backend did not return a job id. Refresh to retry.");
                return;
            }
            setActiveJob({
                job_id: jobId,
                kind: "revise",
                status: data?.status || "queued",
                started_at: data?.started_at || new Date().toISOString(),
                elapsed_seconds: 0,
            });
            toast.success("Revising Detailed Production Script…");
            pollJob(jobId, "revise");
        } catch (e) {
            const code = e?.response?.data?.detail?.code;
            const detail = e?.response?.data?.detail || {};
            const msg = detail.message || e?.userMessage;
            if (code === "dps_already_running") {
                // iter72.1 — cross-kind aware adoption. The existing job
                // may be a generate (not a revise) on the same project+
                // source — adopt whichever kind the backend reports.
                const existingId = detail.existing_job_id;
                const existingKind = detail.kind || "revise";
                if (existingId) {
                    setActiveJob({
                        job_id: existingId,
                        kind: existingKind,
                        status: detail.status || "running",
                        started_at: detail.started_at || new Date().toISOString(),
                        elapsed_seconds: 0,
                    });
                    toast.info(
                        existingKind === "generate"
                            ? "A generation is already running for this project and source. Watching the existing job."
                            : "A revision is already running for this project and source. Watching the existing job.",
                    );
                    pollJob(existingId, existingKind);
                    return;
                }
            }
            toast.error(msg || "Revision failed");
        } finally {
            setRevising(false);
        }
    };

    const approve = async (status) => {
        if (!current?.id) return;
        setApproving(true);
        try {
            const { data } = await api.post(
                `/projects/${projectId}/detailed-production-script/${current.id}/approve`,
                { approval_status: status },
            );
            setCurrent(data?.doc || null);
            setSummary(data?.summary || {});
            toast.success(`Revision ${status}.`);
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail?.message || e?.userMessage || "Approval failed");
        } finally {
            setApproving(false);
        }
    };

    const out = current?.output || {};
    const warnings = out.warnings || [];

    const sourceLabel = useMemo(() => {
        if (!current) return "";
        const snap = current.source_script_snapshot || {};
        const v = current.source_script_variant || snap.variant || "";
        const l = snap.label || "";
        return l ? `${l} (${v})` : v;
    }, [current]);

    if (loading) {
        return (
            <div className="card-base p-4 text-zinc-500 text-xs" data-testid="dps-loading">
                Loading Detailed Production Script…
            </div>
        );
    }

    return (
        <div
            className="card-base p-5 space-y-4"
            data-testid="detailed-production-script-panel"
        >
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <FileText className="w-4 h-4 text-zinc-500" />
                    <h3 className="font-display text-lg">Detailed Production Script</h3>
                    <span className="text-[10px] font-mono uppercase tracking-wider text-zinc-500">
                        Character Dialogue Map · iter70
                    </span>
                    {current && <StatusPill status={current.status} />}
                    {current && (
                        <span className="text-[10px] font-mono text-zinc-500"
                              data-testid="dps-current-revision">
                            rev {current.revision_number}
                        </span>
                    )}
                </div>
                <button
                    type="button"
                    onClick={load}
                    className="btn-secondary text-xs"
                    data-testid="dps-refresh"
                >
                    <RefreshCw className="w-3.5 h-3.5" /> Refresh
                </button>
            </div>

            {!current && (
                <div className="border border-amber-500/40 bg-amber-500/5 rounded-sm p-3"
                     data-testid="dps-storyboard-lock-banner">
                    <p className="text-[12px] text-amber-200 mb-1 font-display">
                        Storyboard AUTO is locked
                    </p>
                    <p className="text-[11px] text-amber-300/80 italic">
                        AUTO pipeline pauses here until a Detailed Production Script
                        is generated AND its current revision is approved. Manual
                        Storyboard runs on legacy projects can override with an
                        explicit confirmation modal — but AUTO never silently falls
                        back to the compact short script.
                    </p>
                </div>
            )}
            {!current && (
                <div className="border border-zinc-800 rounded-sm bg-zinc-950 p-3">
                    <p className="text-[12px] text-zinc-300 mb-1 font-display">
                        Expand the approved short script into a detailed shoot plan.
                    </p>
                    <p className="text-[11px] text-zinc-500 italic">
                        Adds a timecoded breakdown, character dialogue map, environment
                        map, and reference requirement list. Required by AUTO before
                        Storyboard. Old projects may still run Storyboard manually
                        via the override confirm modal.
                    </p>
                </div>
            )}

            {current && sourceLabel && (
                <div className="text-[11px] text-zinc-400" data-testid="dps-source-label">
                    <span className="text-zinc-500">Source variant:</span>{" "}
                    <span className="font-mono text-zinc-200">{sourceLabel}</span>
                </div>
            )}

            {/* Controls: Generate OR Revise */}
            <div className="border border-zinc-800 rounded-sm bg-zinc-950 p-3 space-y-2">
                <label className="text-[11px] text-zinc-400 block">
                    Director Notes
                    <textarea
                        className="input-base w-full text-xs mt-1"
                        rows={3}
                        placeholder={current
                            ? "e.g. Make the opening more emotional. Reduce voiceover and add one trainer dialogue. The child should not speak."
                            : "Optional. Leave empty for a clean first pass."}
                        value={directorNote}
                        onChange={(e) => setDirectorNote(e.target.value)}
                        data-testid="dps-director-note"
                    />
                </label>
                <div className="flex items-center gap-2 flex-wrap">
                    {!current && (
                        <label className="text-[11px] text-zinc-400 inline-flex items-center gap-1">
                            Source variant (optional)
                            <input
                                className="input-base text-xs ml-1 inline-block w-auto font-mono"
                                placeholder="performance | emotional | short_hook"
                                value={explicitVariant}
                                onChange={(e) => setExplicitVariant(e.target.value)}
                                data-testid="dps-explicit-variant"
                            />
                        </label>
                    )}
                    <label className="text-[11px] text-zinc-400 inline-flex items-center gap-1"
                           title="Generate from an unapproved variant. Output is tagged as draft.">
                        <input
                            type="checkbox"
                            checked={allowDraft}
                            onChange={(e) => setAllowDraft(e.target.checked)}
                            data-testid="dps-allow-draft"
                        />
                        Allow draft (unapproved variant)
                    </label>
                    <span className="text-[10px] font-mono text-amber-400/80 italic"
                          data-testid="dps-paid-hint">
                        Paid LLM action
                    </span>
                    {!current ? (
                        <button
                            type="button"
                            onClick={generate}
                            disabled={generating || !!activeJob}
                            className="btn-primary text-xs ml-auto"
                            data-testid="dps-generate-btn"
                        >
                            {(generating || activeJob?.kind === "generate")
                                ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                : <Sparkles className="w-3.5 h-3.5" />}
                            Generate Detailed Production Script
                        </button>
                    ) : (
                        <div className="flex items-center gap-2 ml-auto">
                            <button
                                type="button"
                                onClick={revise}
                                disabled={revising || !directorNote.trim() || !!activeJob}
                                className="btn-primary text-xs"
                                data-testid="dps-revise-btn"
                            >
                                {(revising || activeJob?.kind === "revise")
                                    ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                    : <Sparkles className="w-3.5 h-3.5" />}
                                Revise with director notes
                            </button>
                        </div>
                    )}
                </div>
                {/* iter72 — async DPS job progress banner. Surfaces the
                    background paid-LLM call so the operator knows the work
                    is happening and never feels prompted to double-click. */}
                {activeJob && (
                    <div
                        className="mt-2 flex items-center gap-2 border border-amber-500/40 bg-amber-500/5 text-amber-200 rounded-sm px-3 py-2 text-[11px]"
                        data-testid="dps-job-progress-banner"
                        role="status"
                    >
                        <Loader2 className="w-3.5 h-3.5 animate-spin shrink-0" />
                        <div className="flex-1">
                            <div className="font-display text-amber-100" data-testid="dps-job-progress-message">
                                {activeJob.kind === "revise"
                                    ? "Revising Detailed Production Script…"
                                    : "Generating Detailed Production Script…"}
                            </div>
                            <div className="text-[10px] font-mono text-amber-300/80" data-testid="dps-job-progress-meta">
                                Status: {activeJob.status || "queued"} · Elapsed: {Math.max(0, activeJob.elapsed_seconds || 0)}s · Safe to wait
                            </div>
                        </div>
                        <span className="text-[10px] font-mono uppercase text-amber-300/80" data-testid="dps-job-id">
                            #{(activeJob.job_id || "").slice(0, 8)}
                        </span>
                    </div>
                )}
            </div>

            {current && (
                <>
                    {/* Approval controls */}
                    {current.is_current && current.status !== "approved" && (
                        <div className="flex items-center gap-2">
                            <button
                                type="button"
                                onClick={() => approve("approved")}
                                disabled={approving}
                                className="btn-primary text-xs"
                                data-testid="dps-approve-btn"
                            >
                                {approving
                                    ? <Loader2 className="w-3 h-3 animate-spin" />
                                    : <CheckCircle2 className="w-3 h-3" />}
                                Approve current revision
                            </button>
                            <button
                                type="button"
                                onClick={() => approve("rejected")}
                                disabled={approving}
                                className="btn-secondary text-xs"
                                data-testid="dps-reject-btn"
                            >
                                <XIcon className="w-3 h-3" /> Reject
                            </button>
                            <span className="text-[10px] text-zinc-500 italic">
                                Approving injects this into the Storyboard prompt context.
                            </span>
                        </div>
                    )}
                    {current.status === "approved" && (
                        <div className="text-[11px] text-emerald-300 italic flex items-center gap-1"
                             data-testid="dps-approved-banner">
                            <CheckCircle2 className="w-3 h-3" />
                            Approved · injected into Storyboard context on next run.
                        </div>
                    )}

                    {warnings.length > 0 && (
                        <div className="border border-amber-500/40 bg-amber-500/5 text-amber-200 rounded-sm p-2 space-y-0.5"
                             data-testid="dps-warnings">
                            <div className="flex items-center gap-1 text-[11px] font-display">
                                <AlertTriangle className="w-3 h-3" /> Warnings
                            </div>
                            <ul className="list-disc list-inside text-[11px]">
                                {warnings.map((w, i) => (
                                    <li key={i} data-testid={`dps-warning-${i}`}>{w}</li>
                                ))}
                            </ul>
                        </div>
                    )}

                    {(out.production_summary) && (
                        <div className="border border-zinc-800 rounded-sm bg-zinc-950/60 p-3"
                             data-testid="dps-production-summary">
                            <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 mb-1">
                                Production summary
                            </div>
                            <p className="text-[12px] text-zinc-200 whitespace-pre-line">
                                {out.production_summary}
                            </p>
                        </div>
                    )}

                    <Section
                        title="Timecoded script breakdown"
                        count={out.timecoded_script_breakdown?.length}
                        testId="dps-section-timecoded"
                    >
                        <TimecodedTable beats={out.timecoded_script_breakdown} />
                    </Section>

                    <Section
                        title="Character appearance map"
                        count={(out.character_appearance_map?.length || 0)
                              + (out.voiceover_only?.length || 0)}
                        testId="dps-section-characters"
                    >
                        <CharacterMap
                            chars={out.character_appearance_map}
                            voiceoverOnly={out.voiceover_only}
                        />
                    </Section>

                    <Section
                        title="Character dialogue map"
                        count={out.character_dialogue_map?.length}
                        defaultOpen={false}
                        testId="dps-section-dialogue"
                    >
                        {(out.character_dialogue_map || []).length === 0 ? (
                            <p className="text-[11px] text-zinc-500 italic">
                                No explicit dialogue lines yet — voiceover-only script?
                            </p>
                        ) : (
                            <div className="space-y-1" data-testid="dps-dialogue-list">
                                {out.character_dialogue_map.map((d, i) => (
                                    <div key={i}
                                         className="text-[11px] border border-zinc-800 rounded-sm bg-zinc-950 px-2 py-1"
                                         data-testid={`dps-dialogue-row-${i}`}>
                                        <span className="font-mono text-zinc-400 text-[10px] mr-2">
                                            {d.beat_id} · {d.speaker_kind}
                                        </span>
                                        <span className="font-display text-zinc-100">{d.speaker_id}</span>
                                        <span className="text-zinc-300 ml-2">: {d.line}</span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </Section>

                    <Section
                        title="Environment map"
                        count={out.environment_map?.length}
                        testId="dps-section-environments"
                    >
                        <EnvironmentMap envs={out.environment_map} />
                    </Section>

                    <Section
                        title="Reference requirement map"
                        count={out.reference_requirement_map?.length}
                        testId="dps-section-references"
                    >
                        <ReferenceRequirementMap refs={out.reference_requirement_map} />
                    </Section>

                    <Section
                        title="Production Continuity hand-off suggestions"
                        count={(out.continuity_handoff_suggestions?.characters?.length || 0)
                              + (out.continuity_handoff_suggestions?.environments?.length || 0)}
                        defaultOpen={false}
                        testId="dps-section-handoff"
                    >
                        <ContinuityHandoffSuggestions
                            projectId={projectId}
                            suggestions={out.continuity_handoff_suggestions}
                            onChanged={onHandoffCreated}
                        />
                    </Section>

                    {(out.assumptions?.length || 0) > 0 && (
                        <Section
                            title="Assumptions"
                            count={out.assumptions.length}
                            defaultOpen={false}
                            testId="dps-section-assumptions"
                        >
                            <ul className="list-disc list-inside text-[11px] text-zinc-400">
                                {out.assumptions.map((a, i) => (
                                    <li key={i} data-testid={`dps-assumption-${i}`}>{a}</li>
                                ))}
                            </ul>
                        </Section>
                    )}

                    {versions.length > 1 && (
                        <Section
                            title="Revision history"
                            count={versions.length}
                            defaultOpen={false}
                            testId="dps-section-versions"
                        >
                            <div className="space-y-1">
                                {versions.map((v) => (
                                    <div key={v.id}
                                         className="flex items-center gap-2 text-[11px] border border-zinc-800 rounded-sm bg-zinc-950 px-2 py-1"
                                         data-testid={`dps-version-row-${v.revision_number}`}>
                                        <Clock className="w-3 h-3 text-zinc-500" />
                                        <span className="font-mono text-zinc-300">rev {v.revision_number}</span>
                                        <StatusPill status={v.status} />
                                        {v.is_current && (
                                            <span className="text-[10px] font-mono uppercase tracking-wider text-emerald-300 bg-emerald-500/10 border border-emerald-500/40 px-1 py-0.5 rounded-sm">
                                                current
                                            </span>
                                        )}
                                        <span className="text-zinc-400 truncate flex-1">
                                            {v.director_note || "(no director note)"}
                                        </span>
                                        <span className="text-[10px] font-mono text-zinc-500">
                                            {v.beats}b · {v.characters}c · {v.environments}e
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </Section>
                    )}
                </>
            )}

            <div
                className="text-[10px] text-zinc-500 italic border-t border-zinc-800 pt-2"
                data-testid="dps-honesty"
            >
                Suggestions only. Nothing here auto-creates approved characters or
                environments. <strong className="text-amber-400/90">AUTO pipelines now require an approved Detailed Production Script before Storyboard.</strong> Manual storyboard runs on legacy projects without a DPS prompt for an explicit override confirmation.
            </div>
        </div>
    );
}
